import React from 'react';
import Button from '../common/Button';
import { formatAddress } from '../../utils/web3';

const Header = ({
  account,
  isConnected,
  isConnecting,
  onConnectWallet,
  onDisconnectWallet,
  onToggleMode,
  mode,
  onSortByPriceAscending,
  onSortByPriceDescending,
  onRequestApproval,
  onShowHowTo,
  onOpenClaimModal,
  onOpenMintModal,
  featureFlags,
}) => {
  return (
    <header className="bg-dark-card backdrop-blur-lg border-b border-dark-border py-5 px-6 sticky top-0 z-40 shadow-xl">
      <div className="container mx-auto">
        {/* Top Row - Title, Badges, and Connect Wallet */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-1">
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-etc-primary via-blue-500 to-purple-600 bg-clip-text text-transparent tracking-tight">
              ETCMC License V2 Marketplace
            </h1>
            <div className="flex items-center space-x-2 mt-1">
              <div className="bg-gradient-to-r from-etc-primary/20 to-etc-secondary/20 backdrop-blur-sm border border-etc-primary/30 px-3 py-1.5 rounded-full shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-etc-primary rounded-full animate-pulse"></div>
                  <span className="text-xs text-etc-primary font-medium">BETA</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border border-blue-500/30 px-3 py-1.5 rounded-full shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse delay-75"></div>
                  <span className="text-xs text-blue-400 font-medium">V2</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm border border-purple-500/30 px-3 py-1.5 rounded-full shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-150"></div>
                  <span className="text-xs text-purple-400 font-medium">LIVE</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Connect Wallet Button - Top Right */}
          <div className="flex items-center">
            {isConnected ? (
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-etc-primary to-etc-secondary px-4 py-2.5 rounded-xl flex items-center border border-etc-primary/30 shadow-lg shadow-etc-primary/20">
                <div className="w-2.5 h-2.5 bg-white rounded-full mr-2.5 animate-pulse"></div>
                <span className="text-sm font-semibold text-white">{formatAddress(account)}</span>
              </div>
              <button
                onClick={onDisconnectWallet}
                className="px-4 py-2.5 bg-dark-surface/80 hover:bg-dark-surface text-text-primary rounded-xl font-medium text-sm transition-all duration-200 border border-dark-border hover:border-red-500/30 shadow-sm"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={onConnectWallet}
              disabled={isConnecting}
              className="px-6 py-3 bg-gradient-to-r from-etc-primary to-etc-secondary hover:from-etc-secondary hover:to-etc-primary text-white rounded-xl font-semibold text-sm transition-all duration-200 shadow-lg shadow-etc-primary/20 hover:shadow-etc-primary/30 border border-etc-primary/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="none" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <span>🔗</span>
                  <span>Connect Wallet</span>
                </>
              )}
            </button>
          )}
          </div>
        </div>

        {/* Navigation Buttons - Centered */}
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          <button
            onClick={onToggleMode}
            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-medium text-sm transition-all duration-200 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 border border-blue-500/30 flex items-center gap-2"
          >
            <span>{mode === 'All' ? '👁️' : '🏠'}</span>
            <span>{mode === 'All' ? 'View My NFTs' : 'View All NFTs'}</span>
          </button>
          
          <button
            onClick={onSortByPriceAscending}
            title="Sort by Price (Low to High)"
            className="px-4 py-2.5 bg-dark-surface/80 hover:bg-dark-surface text-text-primary rounded-xl font-medium text-sm transition-all duration-200 border border-dark-border hover:border-etc-primary/30 flex items-center gap-2 shadow-sm"
          >
            <span>⬆️</span>
            <span>Price</span>
          </button>
          
          <button
            onClick={onSortByPriceDescending}
            title="Sort by Price (High to Low)"
            className="px-4 py-2.5 bg-dark-surface/80 hover:bg-dark-surface text-text-primary rounded-xl font-medium text-sm transition-all duration-200 border border-dark-border hover:border-etc-primary/30 flex items-center gap-2 shadow-sm"
          >
            <span>⬇️</span>
            <span>Price</span>
          </button>
          
          <button
            onClick={onRequestApproval}
            className="px-4 py-2.5 bg-gradient-to-r from-etc-primary to-etc-secondary hover:from-etc-secondary hover:to-etc-primary text-white rounded-xl font-medium text-sm transition-all duration-200 shadow-lg shadow-etc-primary/20 hover:shadow-etc-primary/30 border border-etc-primary/30 flex items-center gap-2"
          >
            <span>✅</span>
            <span>Approve</span>
          </button>
          
          <button
            onClick={onShowHowTo}
            className="px-4 py-2.5 bg-dark-surface/80 hover:bg-dark-surface text-text-primary rounded-xl font-medium text-sm transition-all duration-200 border border-dark-border hover:border-blue-500/30 flex items-center gap-2 shadow-sm"
          >
            <span>❓</span>
            <span>How To</span>
          </button>
          
          <button
            onClick={onOpenClaimModal}
            disabled={!featureFlags?.legacyEnabled}
            title={!featureFlags?.legacyEnabled ? "Legacy claims are currently disabled" : "Claim your legacy licenses"}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
              !featureFlags?.legacyEnabled
                ? 'bg-dark-surface/40 text-text-muted border border-dark-border cursor-not-allowed opacity-50'
                : 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 border border-orange-500/30'
            }`}
          >
            <span>🎫</span>
            <span>Claim Legacy</span>
          </button>
          
          <button
            onClick={onOpenMintModal}
            disabled={!featureFlags?.publicEnabled}
            title={!featureFlags?.publicEnabled ? "Public minting is currently disabled" : "Mint new licenses"}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
              !featureFlags?.publicEnabled
                ? 'bg-dark-surface/40 text-text-muted border border-dark-border cursor-not-allowed opacity-50'
                : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 border border-purple-500/30'
            }`}
          >
            <span>🪙</span>
            <span>Mint License</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
